
import React, { createContext, useContext, useCallback, useRef } from "react";
import { ChatContextType } from "./types/contextTypes";
import { useChatState } from "./hooks/useChatState";
import { useConversationActions } from "./hooks/useConversationActions";
import { useMessageActions } from "./hooks/useMessageActions";
import { Message } from "@/types";

// Create context with default values
const ChatContext = createContext<ChatContextType>({
  messages: [],
  conversations: [],
  currentConversation: null,
  isProcessing: false,
  setMessages: () => {},
  setConversations: () => {},
  setCurrentConversation: () => {},
  setIsProcessing: () => {},
  sendMessage: async () => {},
  createNewConversation: async () => null,
  fetchConversations: async () => {},
  fetchMessages: async () => {},
  deleteConversation: async () => {},
  renameConversation: async () => {},
});

// Export hook for using the context
export const useChat = () => useContext(ChatContext);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 🔒 DEDUPE: Map para garantir mensagens únicas por ID
  const messagesMapRef = useRef<Map<string, Message>>(new Map());
  
  const {
    messages,
    conversations,
    currentConversation,
    isProcessing,
    setMessages: setMessagesRaw,
    setConversations,
    setCurrentConversation,
    setIsProcessing,
    user
  } = useChatState();
  
  // 🔒 DEDUPE: Wrapper para setMessages que usa Map
  const setMessages = useCallback((updater: Message[] | ((prev: Message[]) => Message[])) => {
    setMessagesRaw(prev => {
      const newMessages = typeof updater === 'function' ? updater(prev) : updater;
      
      // Atualizar Map e remover duplicatas
      messagesMapRef.current.clear();
      newMessages.forEach(msg => {
        const key = msg.id || `req:${msg.metadata?.request_id}`;
        messagesMapRef.current.set(key, msg);
      });
      
      return Array.from(messagesMapRef.current.values());
    });
  }, [setMessagesRaw]);

  const {
    fetchConversations: fetchConversationsAction,
    fetchMessages: fetchMessagesAction,
    createNewConversation: createNewConversationAction,
    deleteConversation: deleteConversationAction,
    renameConversation: renameConversationAction,
    updateConversationTemplate: updateConversationTemplateAction
  } = useConversationActions(setMessages, setConversations, setCurrentConversation);

  // Create wrapper for fetchMessages that provides userId automatically
  const fetchMessagesWrapper = useCallback(async (conversationId: string) => {
    if (user) {
      await fetchMessagesAction(conversationId, user.id);
    }
  }, [user, fetchMessagesAction]);

  const { sendMessage } = useMessageActions(
    messages,
    setMessages,
    currentConversation,
    setIsProcessing,
    fetchConversationsAction,
    fetchMessagesWrapper,
    createNewConversationAction,
    conversations
  );

  // Wrap the action methods to automatically provide the userId with useCallback
  const fetchConversations = useCallback(async () => {
    if (user) {
      await fetchConversationsAction(user.id);
    }
  }, [user, fetchConversationsAction]);

  const fetchMessages = useCallback(async (conversationId: string) => {
    if (user) {
      await fetchMessagesAction(conversationId, user.id);
    }
  }, [user, fetchMessagesAction]);

  const createNewConversation = useCallback(async () => {
    if (user) {
      // Clear current state before creating a new conversation
      setMessages([]);
      return await createNewConversationAction(user.id);
    }
    return null;
  }, [user, createNewConversationAction, setMessages]);

  const handleSendMessage = useCallback(async (content: string, file: File | null = null) => {
    if (user) {
      await sendMessage(content, file, user.id);
    }
  }, [user, sendMessage]);

  const handleDeleteConversation = useCallback(async (conversationId: string) => {
    if (user) {
      await deleteConversationAction(conversationId, user.id);
    }
  }, [user, deleteConversationAction]);

  const handleRenameConversation = useCallback(async (conversationId: string, newTitle: string) => {
    if (user) {
      await renameConversationAction(conversationId, newTitle, user.id);
    }
  }, [user, renameConversationAction]);

  const handleUpdateConversationTemplate = useCallback(async (conversationId: string, templateName: string) => {
    if (user) {
      return await updateConversationTemplateAction(conversationId, templateName, user.id);
    }
    return false;
  }, [user, updateConversationTemplateAction]);

  return (
    <ChatContext.Provider
      value={{
        messages,
        conversations,
        currentConversation,
        isProcessing,
        setMessages,
        setConversations,
        setCurrentConversation,
        setIsProcessing,
        sendMessage: handleSendMessage,
        createNewConversation,
        fetchConversations,
        fetchMessages,
        deleteConversation: handleDeleteConversation,
        renameConversation: handleRenameConversation,
        updateConversationTemplate: handleUpdateConversationTemplate,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
